/**
 * Client assignment QuestionnaireResponse (ENG-3764 D1). Assigning a form or assessment to a client means saving the
 * QR the client will fill out; the `portal-task-lifecycle` listener creates the client Task from that save. No staff
 * screen creates a client Task.
 */
import type { MedplumClient } from '@medplum/core';
import type { Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import {
  CHART_DOCUMENT_STATUS_EXT,
  CHART_DOCUMENT_STATUS_PENDING_CLIENT,
  EPISODE_OF_CARE_CONTEXT_EXT,
  TEMPLATE_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  setClientAssignmentRequest,
  type ClientAssignmentRequest,
  type AssignmentTemplateCategory,
} from '@unify/fhir-model/resources/client-assignment';

export interface ClientAssignmentQrInput {
  patientId: string;
  questionnaire: Questionnaire;
  category: AssignmentTemplateCategory;
  /** The assigning Practitioner (`QR.author`). */
  author?: Reference;
  episodeId?: string;
  due?: string;
  /** The assigner's note to the client (becomes `Task.note` and the notification body). */
  note?: string;
  accounts?: Reference[];
}

/** `url|version` when the template has both, else `Questionnaire/<id>`. */
export function questionnaireCanonical(questionnaire: Questionnaire): string {
  return questionnaire.url && questionnaire.version
    ? `${questionnaire.url}|${questionnaire.version}`
    : `Questionnaire/${questionnaire.id}`;
}

export function buildClientAssignmentQr(input: ClientAssignmentQrInput): QuestionnaireResponse {
  const qr: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    questionnaire: questionnaireCanonical(input.questionnaire),
    subject: { reference: `Patient/${input.patientId}` },
    ...(input.author?.reference ? { author: { reference: input.author.reference } } : {}),
    extension: [
      { url: CHART_DOCUMENT_STATUS_EXT, valueCode: CHART_DOCUMENT_STATUS_PENDING_CLIENT },
      ...(input.episodeId
        ? [{ url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: { reference: `EpisodeOfCare/${input.episodeId}` } }]
        : []),
    ],
    meta: {
      tag: [{ system: TEMPLATE_CATEGORY_SYSTEM, code: input.category }],
      ...(input.accounts?.length ? { accounts: input.accounts } : {}),
    },
  };
  const request: ClientAssignmentRequest = { due: input.due, message: input.note };
  return setClientAssignmentRequest(qr, request);
}

/** Save the assignment QR. The caller then waits for the listener's Task with `waitForTaskForQr`. */
export async function saveClientAssignmentQr(
  medplum: MedplumClient,
  input: ClientAssignmentQrInput,
): Promise<QuestionnaireResponse> {
  return medplum.createResource(buildClientAssignmentQr(input));
}
